import { ActivityType } from '@showbie-socrative/socrative-utils';

import { CreateFolderEventAdapter } from '~analytics/adapters/CreateFolderEventAdapter';
import { GradeModifiedEventAdapter } from '~analytics/adapters/GradeModifiedEventAdapter';
import { ActivityState } from '~analytics/adapters/QuestionViewEventAdapter';
import { PassRateAction } from '~analytics/adapters/ReportStats/PassRateEventAdapter';
import { ViewInsightParticipantsEventAdapter } from '~analytics/adapters/ViewInsightParticipantsEventAdapter';

import { ContactSalesEventAdapter } from './adapters/ContactSalesEventAdapter';
import { CreateQuizEventAdapter } from './adapters/CreateQuizEventAdapter';
import { CreateWorkspaceEventAdapter } from './adapters/CreateWorkspaceEventAdapter';
import { ExportAnswerKeyEventAdapter } from './adapters/ExportAnswerKeyEventAdapter';
import { ExtractQuestionsEventAdapter } from './adapters/ExtractQuestionsEventAdapter';
import { FinishActivityEventAdapter } from './adapters/FinishActivityEventAdapter';
import { GenerateAIQuestionsEventAdapter } from './adapters/GenerateAIQuestionsEventAdapter';
import { GenerateQuestionsFromAoIAdapter } from './adapters/GenerateQuestionsFromAoIAdapter';
import { GenerateQuestionsFromSpecificStudentsAdapter } from './adapters/GenerateQuestionsFromSpecificStudentsAdapter';
import { GenerateReportEmailStudentsEventAdapter } from './adapters/GenerateReportEmailStudentsEventAdapter';
import { GenerateReportEventAdapter } from './adapters/GenerateReportEventAdapter';
import { ImportFromTextEventAdapter } from './adapters/ImportFromTextEventAdapter';
import { InsightsGenerateQuestionsCTAEventAdapter } from './adapters/InsightsGenerateQuestionsCTAEventAdapter';
import { JoinWorkspaceEventAdapter } from './adapters/JoinWorkspaceEventAdapter';
import { LaunchExitTicketEventAdapter } from './adapters/LaunchExitTicketEventAdapter';
import { LaunchMultipleChoiceEventAdapter } from './adapters/LaunchMultipleChoiceEventAdapter';
import { LaunchQuizEventAdapter } from './adapters/LaunchQuizEventAdapter';
import { LaunchShortAnswerEventAdapter } from './adapters/LaunchShortAnswerEventAdapter';
import { LaunchSpaceRaceEventAdapter } from './adapters/LaunchSpaceRaceEventAdapter';
import { LaunchTrueFalseEventAdapter } from './adapters/LaunchTrueFalseEventAdapter';
import { LaunchVoteEventAdapter } from './adapters/LaunchVoteEventAdapter';
import { LicenseApplyEventAdapter } from './adapters/LicenseApplyEventAdapter';
import { LicensePurchaseEventAdapter } from './adapters/LicensePurchaseEventAdapter';
import { PaywallTouchEventAdapter } from './adapters/PaywallTouchEventAdapter';
import { QuestionSavedEventAdapter } from './adapters/QuestionSavedEventAdapter';
import {
  QuestionTypeChangeCancelledEventAdapter,
  QuestionTypeChangeConfirmedEventAdapter,
  QuestionTypeChangeInitiatedEventAdapter,
} from './adapters/QuestionTypeChangeEventAdapter';
import { SaveGeneratedQuestionsEventAdapter } from './adapters/SaveGeneratedQuestionsEventAdapter';
import { SpecificStudentSelectedQuestionsGeneratedAdapter } from './adapters/SpecificStudentSelectedQuestionsGeneratedAdapter';
import { sendTeacherAmplitudeEvent } from './amplitude';
import {
  EventTypes,
  QuestionTypes,
  QuestionUiSources,
  RoomTypes,
} from './types';

// Account events
function sendSignUpEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.SignUp, data);
}

// Launch events
function sendLaunchQuizEvent(data: {
  room_type: RoomTypes;
  quiz_type: 'instant_feedback' | 'open_navigation' | 'teacher_paced';
  activity_require_names: boolean;
  activity_shuffle_questions: boolean;
  activity_shuffle_answers: boolean;
  activity_show_question_feedback: boolean;
  activity_show_final_score: boolean;
  activity_one_attempt: boolean;
  activity_timer_duration_seconds: number | null;
}) {
  sendTeacherAmplitudeEvent(EventTypes.LaunchQuiz, data);
}

function sendLaunchSpaceRaceEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.LaunchSpaceRace, data);
}

function sendLaunchShortAnswerEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.LaunchShortAnswer, data);
}

function sendLaunchMultipleChoiceEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.LaunchMultipleChoice, data);
}

function sendLaunchTrueFalseEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.LaunchTrueFalse, data);
}

function sendLaunchExitTicketEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.LaunchExitTicket, data);
}

function sendLaunchVoteEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.LaunchVote, data);
}

interface ActivityEventAdapterParams {
  room_type: RoomTypes;
  quiz_type: 'instant_feedback' | 'open_navigation' | 'teacher_paced';
  activity_require_names: boolean;
  activity_shuffle_questions: boolean;
  activity_shuffle_answers: boolean;
  activity_show_question_feedback: boolean;
  activity_show_final_score: boolean;
  activity_one_attempt: boolean;
}

interface PauseActivityEventAdapterParams extends ActivityEventAdapterParams {
  action_type: 'pause' | 'resume';
}

function sendFinishActivityEvent(data: ActivityEventAdapterParams) {
  sendTeacherAmplitudeEvent(EventTypes.FinishActivity, data);
}

function sendPauseActivityEvent(data: PauseActivityEventAdapterParams) {
  if (data.action_type === 'pause') {
    sendTeacherAmplitudeEvent(EventTypes.PauseActivity, data);
  } else if (data.action_type === 'resume') {
    sendTeacherAmplitudeEvent(EventTypes.ResumeActivity, data);
  }
}

function sendQuestionViewClickEvent(data: {
  question_type: QuestionTypes | null;
  activity_state: ActivityState;
}) {
  sendTeacherAmplitudeEvent(EventTypes.QuestionViewClick, data);
}

// Hand raise events (SOC-5588)
//
// Fired from the teacher client on PubNub confirmation of the underlying
// hand-raise message (never a REST response), so each student/teacher action is
// counted exactly once. No student identifiers are attached — analytics live
// only on the teacher end per the privacy policy, and the teacher already
// observes every hand-raise message on the room channel.
function sendHandRaiseRaisedEvent(data: { room_type: RoomTypes }) {
  sendTeacherAmplitudeEvent(EventTypes.HandRaiseRaised, data);
}

function sendHandRaiseLoweredBySelfEvent(data: { room_type: RoomTypes }) {
  sendTeacherAmplitudeEvent(EventTypes.HandRaiseLoweredBySelf, data);
}

function sendHandRaiseLoweredByTeacherEvent(data: { room_type: RoomTypes }) {
  sendTeacherAmplitudeEvent(EventTypes.HandRaiseLoweredByTeacher, data);
}

function sendHandRaiseSettingsChangeEvent(data: {
  room_type: RoomTypes;
  enabled: boolean;
}) {
  sendTeacherAmplitudeEvent(EventTypes.HandRaiseSettingsChange, data);
}

function sendQuestionSavedEvent(data: {
  is_new_question: boolean;
  question_type: QuestionTypes | null;
  ui_source: QuestionUiSources;
  has_code_block: boolean;
  has_ordered_list: boolean;
  has_unordered_list: boolean;
  original_question_weight: number | undefined;
  saved_question_weight: number;
  original_has_explanation_image: boolean;
  saved_has_explanation_image: boolean;
  original_has_question_image: boolean;
  saved_has_question_image: boolean;
  original_answer_count: number;
  saved_answer_count: number;
  saved_formula_count: number;
  saved_chemical_count: number;
  original_formula_count: number;
  original_chemical_count: number;
  original_has_question_video: boolean;
  saved_has_question_video: boolean;
  generation_source: string | null;
}) {
  sendTeacherAmplitudeEvent(EventTypes.QuestionSaved, data);
}

// Quiz events
function sendCreateQuizEvent(data: {
  creation_method:
    | 'app'
    | 'excel'
    | 'code'
    | 'link'
    | 'merge'
    | 'copy'
    | 'from_workspace'
    | 'to_workspace';
  source_workspace_id: string | null;
  destination_workspace_id: string | null;
}) {
  sendTeacherAmplitudeEvent(EventTypes.CreateQuiz, data);
}

function sendCreateFolderEvent(data: { workspace_id?: string }) {
  sendTeacherAmplitudeEvent(EventTypes.CreateFolder, data);
}

// Report events
function sendGenerateReportEvent(data: {
  report_type:
    | 'complete_results'
    | 'individual_students'
    | 'results_summary'
    | 'answer_key';
  delivery_method: 'email' | 'email_to_students' | 'save_to_drive' | 'download';
  option_omit_mc_answer_content: boolean;
  option_include_explanations: boolean;
  options_include_correct_answers: boolean;
  email_to_students_recipient_count;
  activity_student_count: number;
  activity_question_count: number;
  activity_room_type: RoomTypes;
}) {
  sendTeacherAmplitudeEvent(EventTypes.GenerateReport, data);
}

function sendPassRateEvent(data: {
  action: PassRateAction;
  value?: number | null;
}) {
  sendTeacherAmplitudeEvent(EventTypes.PassRate, data);
}

function sendGradeModifiedEvent(data: {
  question_type: QuestionTypes | null;
  question_weight: number;
  original_correctness: 'partial' | 'correct' | 'incorrect' | null;
  modified_correctness: 'partial' | 'correct' | 'incorrect' | null;
  original_score: number;
  modified_score: number;
  activity_type: ActivityType;
  activity_student_count: number;
  activity_question_count: number;
  activity_room_type: RoomTypes;
  activity_end_date: Date;
}) {
  sendTeacherAmplitudeEvent(EventTypes.GradeModified, data);
}

// Paywall / Purchase events
function sendPaywallTouchEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.PaywallTouch, data);
}

function sendPurchaseEvent(data: {
  id: string;
  license_count: string;
  years: number;
  coupon_code: string;
  price: number;
  referrer: string;
  referrer_paywall_feature: string;
  applied_seat_to_self: boolean;
}) {
  sendTeacherAmplitudeEvent(EventTypes.Purchase, data);
}

function sendApplyLicenseEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.ApplyLicense, data);
}

function sendContactSalesEvent(data: { form_source: 'typeform' | 'api' }) {
  sendTeacherAmplitudeEvent(EventTypes.ContactSales, data);
}

// Workspace events
function sendCreateWorkspaceEvent(data: { workspace_id: string }) {
  sendTeacherAmplitudeEvent(EventTypes.CreateWorkspace, data);
}

function sendJoinWorkspaceEvent(data: {
  workspace_id: string;
  join_method?: 'code' | 'link';
}) {
  sendTeacherAmplitudeEvent(EventTypes.JoinWorkspace, data);
}

// AI events
function sendGenerateAIQuestionsEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.GenerateAIQuestions, data);
}

function sendBulkAddQuestionsEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.BulkAddQuestions, data);
}

function sendImportFromTextEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.ImportFromTextWithAI, data);
}

function sendExtractQuestionsEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.ExtractQuestions, data);
}

// Insights events
function sendViewInsightParticipantsEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.ViewInsightParticipants, data);
}

function sendClickCTAGenerateAIQuestionsInsightsEvent(data = {}) {
  sendTeacherAmplitudeEvent(
    EventTypes.ClickCTAGenerateAIQuestionsInsights,
    data
  );
}

function sendSaveGeneratedQuestionsEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.SaveAiGeneratedQuestions, data);
}

function sendGenerateQuestionsFromAoIEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.GenerateAiQuestionsFromAoI, data);
}

function sendGenerateQuestionsFromSpecificStudentsEvent(data = {}) {
  sendTeacherAmplitudeEvent(
    EventTypes.GenerateAIQuestionsFromSpecificStudents,
    data
  );
}

function sendAoIQuestionsGeneratedEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.AoIQuestionsGenerated, data);
}

function sendSpecificStudentQuestionsGeneratedEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.SpecificStudentQuestionsGenerated, data);
}

export {
  sendSignUpEvent,
  sendLaunchQuizEvent,
  sendLaunchSpaceRaceEvent,
  sendLaunchShortAnswerEvent,
  sendLaunchMultipleChoiceEvent,
  sendLaunchTrueFalseEvent,
  sendLaunchExitTicketEvent,
  sendLaunchVoteEvent,
  sendFinishActivityEvent,
  sendPauseActivityEvent,
  sendGenerateReportEvent,
  sendPaywallTouchEvent,
  sendPurchaseEvent,
  sendApplyLicenseEvent,
  sendGradeModifiedEvent,
  sendPassRateEvent,
  sendQuestionSavedEvent,
  sendQuestionViewClickEvent,
  sendCreateQuizEvent,
  sendContactSalesEvent,
  sendCreateWorkspaceEvent,
  sendJoinWorkspaceEvent,
  sendCreateFolderEvent,
  sendGenerateAIQuestionsEvent,
  sendBulkAddQuestionsEvent,
  sendImportFromTextEvent,
  sendExtractQuestionsEvent,
  sendViewInsightParticipantsEvent,
  sendClickCTAGenerateAIQuestionsInsightsEvent,
  sendSaveGeneratedQuestionsEvent,
  sendGenerateQuestionsFromSpecificStudentsEvent,
  sendAoIQuestionsGeneratedEvent,
  sendSpecificStudentQuestionsGeneratedEvent,
  sendGenerateQuestionsFromAoIEvent,
  sendHandRaiseRaisedEvent,
  sendHandRaiseLoweredBySelfEvent,
  sendHandRaiseLoweredByTeacherEvent,
  sendHandRaiseSettingsChangeEvent,
  // Adapters
  LaunchQuizEventAdapter,
  LaunchSpaceRaceEventAdapter,
  LaunchMultipleChoiceEventAdapter,
  LaunchTrueFalseEventAdapter,
  LaunchShortAnswerEventAdapter,
  LaunchExitTicketEventAdapter,
  LaunchVoteEventAdapter,
  FinishActivityEventAdapter,
  LicensePurchaseEventAdapter,
  LicenseApplyEventAdapter,
  GenerateReportEventAdapter,
  GenerateReportEmailStudentsEventAdapter,
  GradeModifiedEventAdapter,
  PaywallTouchEventAdapter,
  QuestionSavedEventAdapter,
  CreateQuizEventAdapter,
  ContactSalesEventAdapter,
  CreateWorkspaceEventAdapter,
  JoinWorkspaceEventAdapter,
  CreateFolderEventAdapter,
  GenerateAIQuestionsEventAdapter,
  ImportFromTextEventAdapter,
  ExtractQuestionsEventAdapter,
  ExportAnswerKeyEventAdapter,
  ViewInsightParticipantsEventAdapter,
  InsightsGenerateQuestionsCTAEventAdapter,
  SaveGeneratedQuestionsEventAdapter,
  SpecificStudentSelectedQuestionsGeneratedAdapter,
  GenerateQuestionsFromAoIAdapter,
  GenerateQuestionsFromSpecificStudentsAdapter,
  QuestionTypeChangeInitiatedEventAdapter,
  QuestionTypeChangeConfirmedEventAdapter,
  QuestionTypeChangeCancelledEventAdapter,
};
